<template>
    <div v-if="property">
        <!-- TODO: If the user does not sell houses then they shouldn't see the 'unconfirmed' events -->

        <b-row class="mb-5">
            <b-col
                cols="12"
                lg="8"
                xl="9"
            >
                <b-row>
                    <b-col
                        cols="12"
                        xl="11"
                    >
                        <h1 class="mb-1">
                            {{ property.full_address }}
                        </h1>
                        <p
                            v-if="property.listed_on"
                            class="mb-0"
                        >
                            Listed on {{ property.listed_on | moment('L') }}
                        </p>
                    </b-col>
                </b-row>
            </b-col>
            <b-col
                cols="12"
                lg="4"
                xl="3"
            >
                <p class="text-bold color-dark-grey mb-1 mt-4 mt-lg-0">
                    For {{ getTransactionType(property.transaction_type) }}
                </p>
                <p
                    v-if="property.price_prefix"
                    class="mb-1"
                >
                    {{ property.price_prefix }}
                </p>
                <p class="h1 color-secondary mb-0">
                    {{ property.price | price(false) }}
                </p>
            </b-col>
        </b-row>


        <b-row>
            <b-col
                cols="12"
                lg="8"
                xl="9"
            >
                <!-- Gallery -->
                <div
                    v-if="property.assets && property.assets.gallery_image && property.assets.gallery_image.length"
                    class="card"
                >
                    <div class="card__body">
                        <h2 class="card__title">
                            Gallery
                        </h2>

                        <flickity
                            ref="flickity"
                            class="flickity-main"
                            :options="flickityOptions"
                        >
                            <div
                                v-for="image in property.assets.gallery_image"
                                :key="image.id"
                            >
                                <img
                                    :src="image.src"
                                    class="img-responsive"
                                >
                            </div>
                        </flickity>

                        <flickity
                            ref="flickityNav"
                            class="flickity-nav"
                            :options="flickityNavOptions"
                        >
                            <div
                                v-for="image in property.assets.gallery_image"
                                :key="image.id"
                            >
                                <img
                                    :src="image.src"
                                    class="img-responsive"
                                >
                            </div>
                        </flickity>
                    </div>
                </div>

                <!-- Description -->
                <div class="mb-5">
                    <div class="py-5">
                        <h2 class="mb-5">
                            Description
                        </h2>
                        <ul class="list-unstyled mb-5 property-features">
                            <li v-if="property.area">
                                <SVGSprite
                                    name="blueprint"
                                    class="feature-icon"
                                />
                                <span>
                                    <span class="text-bold color-dark-grey d-block">Area</span>
                                    <span class="d-block p-sm">{{ property.area }}</span>
                                </span>
                            </li>
                            <li v-if="property.bedrooms === 0 || property.bedrooms">
                                <SVGSprite
                                    name="bed"
                                    class="feature-icon"
                                />
                                <span>
                                    <span class="text-bold color-dark-grey d-block">Beds</span>
                                    <span class="d-block p-sm">{{ property.bedrooms.toString() + ' Bedroom' + (property.bedrooms !== 1 ? 's' : '') }}</span>
                                </span>
                            </li>
                            <li v-if="property.bathrooms === 0 || property.bathrooms">
                                <SVGSprite
                                    name="bathtub"
                                    class="feature-icon"
                                />
                                <span>
                                    <span class="text-bold color-dark-grey d-block">Baths</span>
                                    <span class="d-block p-sm">{{ property.bathrooms.toString() + ' Bathroom' + (property.bathrooms !== 1 ? 's' : '') }}</span>
                                </span>
                            </li>
                            <li v-if="property.receptions === 0 || property.receptions">
                                <SVGSprite
                                    name="living-room"
                                    class="feature-icon"
                                />
                                <span>
                                    <span class="text-bold color-dark-grey d-block">Reception</span>
                                    <span class="d-block p-sm">{{ property.receptions.toString() + ' Reception Room' + (property.bedrooms !== 1 ? 's' : '') }}</span>
                                </span>
                            </li>
                            <li v-if="property.floors === 0 || property.floors">
                                <SVGSprite
                                    name="stairs"
                                    class="feature-icon"
                                />
                                <span>
                                    <span class="text-bold color-dark-grey d-block">Floors</span>
                                    <span class="d-block p-sm">{{ property.floors.toString() + ' Floor' + (property.floors !== 1 ? 's' : '') }}</span>
                                </span>
                            </li>
                            <li v-if="garage">
                                <SVGSprite
                                    name="garage"
                                    class="feature-icon"
                                />
                                <span>
                                    <span class="text-bold color-dark-grey d-block">Garage</span>
                                    <span class="d-block p-sm">{{ garage }}</span>
                                </span>
                            </li>
                        </ul>
                        <div
                            v-if="property.description"
                            class="mb-5"
                            v-html="property.description"
                        />
                    </div>
                    <div
                        v-if="property.transaction_type === 'let'"
                        class="py-5"
                    >
                        <h2 class="mb-5">
                            Payment information
                        </h2>

                        <h3>Before the tenancy starts</h3>
                        <ul>
                            <li>Holding Deposit: {{ property.holding_deposit }}</li>
                            <li>Deposit: {{ property.holding_deposit }}</li>
                        </ul>

                        <h3>During the tenancy</h3>
                        <ul v-if="property.country === 'Wales'">
                            <li>Payment of {{ property.late_payment_amount | price }} for the late payment of rent</li>
                            <li>Payment for breach of the tenancy agreement up to the prescribed limit.</li>
                        </ul>
                        <ul v-else>
                            <li>Payment of up to {{ property.change_agreement_amount | price }} if you want to change the tenancy agreement.</li>
                            <li>Payment of interest for the late payment of rent of {{ property.late_payment_interest_rate }}%.</li>
                            <li>Payment of {{ property.lost_key_payment | price }} for the reasonably incurred costs for the loss of keys / security devices.</li>
                            <li>Payment for unpaid rent or other reasonable costs associated with the early termination of the tenancy.</li>
                        </ul>

                        <h3>Payable to the provider (if permitted and applicable)</h3>
                        <ul>
                            <li>Utilities - gas, electricity, water</li>
                            <li>Communications - telephone and broadband</li>
                            <li>Installation of cable / satellite</li>
                            <li>Subscription to cable / satellite supplier</li>
                            <li>Television licence</li>
                            <li>Council Tax</li>
                        </ul>

                        <h3>Other permitted payments</h3>
                        <ul>
                            <li>Any other permitted payments, not included above, under the relevant legislation including contractual damages.</li>
                            <li>Tenant protection.</li>
                        </ul>
                        <p>My Online Estate Agent is a member of CMP which is a client money protection scheme, reg number CMP003263 and also a member of PRS, which is a redress scheme reg number PRS015091. You can find out more details of the agent’s website or by contracting the agent directly.</p>
                        <p>Please note that letting agents are required by law to publish on their websites information for potential tenants about relevant fees, redress schemes and client money protection schemes (including the name of those schemes). Relevant fees must also be published on third party websites, such as Rightmove. For properties to rent in England, details of the agent’s membership of any redress scheme and client money protection scheme must also be published with their fees on Rightmove. It is the agent’s responsibility to ensure that relevant information is provided to Rightmove and is up to date and accurate. If the relevant information does not appear here, the agent may have included it within the property description.</p>
                    </div>
                </div>

                <!-- Location -->
                <div class="card">
                    <div class="card__body pb-0">
                        <h2 class="card__title">
                            Location
                        </h2>
                        <p><span class="text-bold color-dark-grey">Address: </span>{{ property.full_address }}</p>
                        <div class="image-thumbnail image-thumbnail--rect rounded-corners mb-5">
                            <iframe
                                :src="locationMap"
                                class="location-map"
                                width="600"
                                height="600"
                            />
                        </div>
                    </div>
                </div>


                <b-row>
                    <b-col
                        xl="4"
                        cols="12"
                        class="mb-5"
                    >
                        <!-- Floorplan -->
                        <div
                            v-if="property.assets.floorplan && property.assets.floorplan.src"
                            class="card h-100"
                        >
                            <div class="card__body d-flex flex-column">
                                <h2 class="card__title">
                                    Floorplan
                                </h2>
                                <p class="mb-4">
                                    A floorplan shows a birds eye view layout of the rooms and floors of a property.
                                </p>
                                <div
                                    v-if="property.assets.floorplan.variants && property.assets.floorplan.variants.image_medium"
                                    class="image-thumbnail image-thumbnail--rect rounded-corners mb-4"
                                >
                                    <div>
                                        <img :src="property.assets.floorplan.variants.image_medium">
                                    </div>
                                </div>
                                <a
                                    :href="property.assets.floorplan.src"
                                    class="btn btn--secondary mt-auto"
                                    target="_blank"
                                >
                                    View Floorplan
                                </a>
                            </div>
                        </div>
                    </b-col>
                    <b-col
                        xl="4"
                        cols="12"
                        class="mb-5"
                    >
                        <!-- EPC -->
                        <div
                            v-if="property.assets.epc && property.assets.epc.src"
                            class="card h-100"
                        >
                            <div class="card__body d-flex flex-column">
                                <h2 class="card__title">
                                    {{ property.country === 'Scotland' ? 'Home Report' : 'EPC' }}
                                </h2>
                                <p class="mb-4">
                                    {{ property.country === 'Scotland' ? 'A Home Report' : 'An Energy Performance Certificate' }} shows the properties energy efficiency rating.
                                </p>
                                <div
                                    v-if="property.assets.epc.variants && property.assets.epc.variants.image_medium"
                                    class="image-thumbnail image-thumbnail--rect rounded-corners mb-4"
                                >
                                    <div>
                                        <img :src="property.assets.epc.variants.image_medium">
                                    </div>
                                </div>
                                <a
                                    :href="property.assets.epc.src"
                                    class="btn btn--secondary mt-auto"
                                    target="_blank"
                                >
                                    View EPC
                                </a>
                            </div>
                        </div>
                    </b-col>
                    <b-col
                        v-if="property.transaction_type === 'let' && property.assets.gsc && property.assets.gsc.src"
                        xl="4"
                        cols="12"
                        class="mb-5"
                    >
                        <!-- GSC -->
                        <div
                            class="card h-100"
                        >
                            <div class="card__body d-flex flex-column">
                                <h2 class="card__title">
                                    GSC
                                </h2>
                                <p class="mb-4">
                                    A Gas Safety Certificate verifies that the properties gas installations are safe to use.
                                </p>
                                <div
                                    v-if="property.assets.gsc.variants && property.assets.gsc.variants.image_medium"
                                    class="image-thumbnail image-thumbnail--rect rounded-corners mb-4"
                                >
                                    <div>
                                        <img :src="property.assets.gsc.variants.image_medium">
                                    </div>
                                </div>
                                <a
                                    :href="property.assets.gsc.src"
                                    class="btn btn--secondary mt-auto"
                                    target="_blank"
                                >
                                    View GSC
                                </a>
                            </div>
                        </div>
                    </b-col>
                </b-row>

                <!-- Video -->
                <div
                    v-if="property.interactive_floorplan"
                    class="card card--video"
                >
                    <div class="card__body">
                        <h2 class="card__title">
                            Interactive Walkthrough
                        </h2>
                        <b-embed
                            type="iframe"
                            aspect="16by9"
                            :src="property.interactive_floorplan"
                            allowfullscreen
                        />
                    </div>
                </div>
            </b-col>
            <b-col
                cols="12"
                lg="4"
                xl="3"
                class="sidebar-sticky"
            >
                <div class="card">
                    <div class="card__body text-center">
                        <div class="mb-3">
                            <b-btn
                                class="btn d-none d-lg-inline-block"
                                href="javascript:window.print()"
                            >
                                Print Property
                            </b-btn>
                        </div>
                        <div
                            v-if="!property.is_user_property"
                            class="mb-3"
                        >
                            <b-btn
                                v-b-modal.modal-request
                                class="btn btn--secondary"
                            >
                                Request Viewing
                            </b-btn>
                        </div>
                        <div
                            v-if="!property.is_user_property"
                            class="mb-3"
                        >
                            <router-link
                                :to="{ name: 'new-message-thread', params: { toId: property.id, toType: 'property' } }"
                                class="btn btn--secondary"
                            >
                                Contact Owner
                            </router-link>
                        </div>
                        <div
                            v-if="!property.is_user_property && property.transaction_type === 'sale'"
                            class="mb-5"
                        >
                            <b-btn
                                v-b-modal.modal-offer
                                class="btn btn--primary"
                                @click="prepopulateOfferForm"
                            >
                                Make an offer
                            </b-btn>
                        </div>
                        <div>
                            <p class="text-left mb-0">
                                Making an offer has never been easier: once you have clicked the offer button, you will be taken through step by step the offer process, beginning with our initial offer pop-up form.
                            </p>
                        </div>
                    </div>
                </div>
            </b-col>
        </b-row>

        <b-modal
            id="modal-request"
            ref="requestModal"
            size="lg"
            hide-footer
            title="Request Viewing"
        >
            <RequestViewing
                :property="property"
                @success="$refs.requestModal.hide()"
            />
        </b-modal>

        <b-modal
            id="modal-offer"
            ref="offerModal"
            size="lg"
            hide-footer
            title="Make an Offer"
        >
            <b-row class="mb-5">
                <b-col
                    cols="12"
                >
                    <p>Please fill in your details to this form to officially begin your property purchase journey. Once you have submitted your offer, we will double check the details before finally delivering your offer to the property owner</p>
                </b-col>
            </b-row>

            <b-row>
                <b-col
                    cols="12"
                >
                    <!-- Field: Offer Amount -->
                    <div class="field-wrap">
                        <label
                            class="h4 text-bold color-mid-grey mb-3"
                            for="offer-amount"
                        >
                            Offer Amount
                        </label>
                        <div class="field-wrap__input field-wrap__input--prepend field-wrap__input--prepend-fill">
                            <input
                                id="offer-amount"
                                v-model="makeOffer.amount"
                                v-validate="{ required: true, numeric: true, min_value: 0 }"
                                type="number"
                                name="offer-amount"
                                placeholder="0"
                            >
                            <div class="field-wrap__input-icon">
                                <SVGSprite
                                    name="gbp"
                                    class="form-icon"
                                />
                            </div>
                        </div>
                        <div
                            v-show="errors.has('offer-buyer')"
                            class="validation-error pt-4"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                {{ errors.first('offer-buyer') }}
                            </div>
                        </div>
                    </div>
                    <!-- END Field: Offer Amount -->

                    <!-- Field: Buyer Position -->
                    <div class="field-wrap">
                        <label
                            class="h4 text-bold color-mid-grey mb-3"
                            for="offer-buyer"
                        >
                            Buyer Position
                        </label>
                        <div class="field-wrap__input field-wrap__input--select mb-4">
                            <select
                                id="offer-buyer"
                                v-model="makeOffer.buyer"
                                v-validate="{ required: true, included: buyerPositions.map(item => item.name) }"
                                name="offer-buyer"
                            >
                                <option
                                    :value="null"
                                    disabled
                                    selected
                                    hidden
                                >
                                    Select a value
                                </option>
                                <option
                                    v-for="buyerPosition in buyerPositions"
                                    :key="buyerPosition.id"
                                    :value="buyerPosition.name"
                                >
                                    {{ buyerPosition.name }}
                                </option>
                                <option>Test</option>
                            </select>
                        </div>
                        <div
                            v-show="errors.has('offer-buyer')"
                            class="validation-error pt-4"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                {{ errors.first('offer-buyer') }}
                            </div>
                        </div>
                    </div>
                    <!-- END Field: Buyer Position -->

                    <!-- Field: Conditions -->
                    <div class="field-wrap">
                        <label
                            class="h4 text-bold color-mid-grey mb-3"
                            for="offer-conditions"
                        >
                            Conditions of Offer
                        </label>
                        <div class="field-wrap__input">
                            <textarea
                                id="offer-conditions"
                                v-model="makeOffer.conditions"
                                type="textarea"
                                name="offer-conditions"
                                placeholder=""
                                rows="10"
                            />
                        </div>
                    </div>
                    <!-- END Field: Conditions -->
                </b-col>
            </b-row>

            <b-row>
                <b-col
                    cols="12"
                >
                    <!-- First name -->
                    <div class="field-wrap">
                        <label
                            class="h4 text-bold color-mid-grey mb-3"
                            for="offer-name"
                        >
                            Name
                        </label>
                        <div class="field-wrap__input">
                            <input
                                id="offer-name"
                                v-model="makeOffer.name"
                                v-validate="{ required: true }"
                                type="text"
                                name="offer-name"
                                placeholder=""
                            >
                        </div>
                        <div
                            v-show="errors.has('offer-name')"
                            class="validation-error pt-4"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                {{ errors.first('offer-name') }}
                            </div>
                        </div>
                    </div>
                    <!-- END First name -->
                </b-col>
            </b-row>

            <!-- Sub heading -->
            <h2 class="h4 mb-3 color-mid-grey text-bold">
                Address
            </h2>

            <b-row
                v-if="addresses.length"
            >
                <b-col
                    cols="12"
                >
                    <div class="field-wrap">
                        <div class="field-wrap__input field-wrap__input--select mb-5">
                            <select
                                id="field-selectaddress"
                                v-model="makeOffer.address.id"
                                v-validate="{ required: true }"
                                :class="{ 'has-error': errors.has('field-selectaddress') }"
                                name="field-selectaddress"
                            >
                                <option
                                    :value="null"
                                    disabled
                                    selected
                                    hidden
                                >
                                    Select an address
                                </option>
                                <option
                                    v-for="address in addresses"
                                    :key="address.id"
                                    :value="address.id"
                                >
                                    {{ address.label }}
                                </option>
                                <option value="new">
                                    Add a new address
                                </option>
                            </select>
                        </div>
                        <div
                            v-if="errors.has('field-selectaddress')"
                            class="py-3"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                Please select an address option
                            </div>
                        </div>
                    </div>
                </b-col>
            </b-row>

            <b-row v-if="makeOffer.address.id === 'new'">
                <b-col
                    cols="12"
                >
                    <!-- Field: Search address -->
                    <div class="field-wrap">
                        <h4 class="mb-3 color-mid-grey text-bold">
                            Address Lookup
                        </h4>
                        <label
                            for="field-search"
                            class="sr-only"
                        >
                            Postcode
                        </label>
                        <div class="field-wrap__input field-wrap__input--prepend field-wrap__input--prepend-fill">
                            <input
                                id="field-search"
                                v-model="postcodeSearch"
                                type="search"
                                name="field-search"
                                placeholder="Postcode or Location e.g. London"
                            >
                            <div class="field-wrap__input-icon">
                                <SVGSprite
                                    name="search"
                                    class="form-icon"
                                />
                            </div>
                        </div>
                    </div>
                    <button
                        class="btn btn--secondary mb-5"
                        @click.prevent="searchAddress()"
                    >
                        Search Address
                    </button>
                    <!-- END Field: Search address -->
                </b-col>
            </b-row>

            <b-row
                v-if="makeOffer.address.id === 'new'"
                class="pt-3"
            >
                <b-col
                    cols="12"
                    lg="6"
                >
                    <!-- Field: Address Line 1 -->
                    <div class="field-wrap">
                        <h4 class="mb-3 color-mid-grey text-bold">
                            Address Line 1
                        </h4>
                        <label
                            for="field-address-1"
                            class="sr-only"
                        >
                            Address Line 1
                        </label>
                        <div class="field-wrap__input mb-4">
                            <input
                                id="field-address-1"
                                v-model="makeOffer.address.address"
                                v-validate="{ required: true }"
                                :class="{ 'has-error': errors.has('field-address-1') }"
                                type="text"
                                name="field-address-1"
                                placeholder="Address Line 1"
                            >
                        </div>
                        <div
                            v-if="errors.has('field-address-1')"
                            class="py-3"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                Please enter your address
                            </div>
                        </div>
                    </div>
                    <!-- END Field: Address Line 1 -->

                    <!-- Field: Address Line 2 -->
                    <div class="field-wrap">
                        <h4 class="mb-3 color-mid-grey text-bold">
                            Address Line 2
                        </h4>
                        <label
                            for="field-address-2"
                            class="sr-only"
                        >
                            Address Line 2
                        </label>
                        <div class="field-wrap__input mb-4">
                            <input
                                id="field-address-2"
                                v-model="makeOffer.address.address2"
                                :class="{ 'has-error': errors.has('field-address-2') }"
                                type="text"
                                name="field-address-2"
                                placeholder="Address Line 2"
                            >
                        </div>
                    </div>
                    <!-- END Field: Address Line 2 -->

                    <!-- Field: Postcode -->
                    <div class="field-wrap">
                        <h4 class="mb-3 color-mid-grey text-bold">
                            Postcode
                        </h4>
                        <label
                            for="field-postcode"
                            class="sr-only"
                        >
                            Postcode
                        </label>
                        <div class="field-wrap__input mb-4">
                            <input
                                id="field-postcode"
                                v-model="makeOffer.address.postcode"
                                v-validate="{ required: true }"
                                :class="{ 'has-error': errors.has('field-postcode') }"
                                type="text"
                                name="field-postcode"
                                placeholder="Postcode"
                            >
                        </div>
                        <div
                            v-if="errors.has('field-postcode')"
                            class="py-3"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                Please enter your postcode
                            </div>
                        </div>
                        <!-- END Field: Postcode -->
                    </div>
                </b-col>
                <b-col
                    cols="12"
                    lg="6"
                >
                    <!-- Field: Town / City -->
                    <div class="field-wrap">
                        <h4 class="mb-3 color-mid-grey text-bold">
                            Town / City
                        </h4>
                        <label
                            for="field-town-city"
                            class="sr-only"
                        >
                            Town / City
                        </label>
                        <div class="field-wrap__input mb-4">
                            <input
                                id="field-town-city"
                                v-model="makeOffer.address.town"
                                v-validate="{ required: true }"
                                :class="{ 'has-error': errors.has('field-town-city') }"
                                type="text"
                                name="field-town-city"
                                placeholder="Town / City"
                            >
                        </div>
                        <div
                            v-if="errors.has('field-town-city')"
                            class="py-3"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                Please enter your town or city
                            </div>
                        </div>
                    </div>
                    <!-- END Field: Town / City -->

                    <!-- Field: County -->
                    <div class="field-wrap">
                        <h4 class="mb-3 color-mid-grey text-bold">
                            County
                        </h4>
                        <label
                            for="field-county"
                            class="sr-only"
                        >
                            County
                        </label>
                        <div class="field-wrap__input mb-4">
                            <input
                                id="field-county"
                                v-model="makeOffer.address.county"
                                v-validate="{ required: true }"
                                :class="{ 'has-error': errors.has('field-county') }"
                                type="text"
                                name="field-county"
                                placeholder="County"
                            >
                        </div>
                        <div
                            v-if="errors.has('field-county')"
                            class="py-3"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                Please enter your county
                            </div>
                        </div>
                    </div>
                    <!-- END Field: County -->

                    <!-- Field: Country -->
                    <div class="field-wrap">
                        <h4 class="mb-3 color-mid-grey text-bold">
                            Country
                        </h4>
                        <label
                            for="field-country"
                            class="sr-only"
                        >
                            Country
                        </label>
                        <div class="field-wrap__input field-wrap__input--select mb-4">
                            <select
                                id="field-country"
                                v-model="makeOffer.address.country"
                                v-validate="{ required: true }"
                                :class="{ 'has-error': errors.has('field-country') }"
                                name="field-country"
                            >
                                <option
                                    :value="null"
                                    disabled
                                    selected
                                    hidden
                                >
                                    Select a value
                                </option>
                                <option
                                    :value="null"
                                    disabled
                                    hidden
                                    selected
                                >
                                    Select a country
                                </option>
                                <option>England</option>
                                <option>Wales</option>
                                <option>Scotland</option>
                            </select>
                        </div>
                        <div
                            v-if="errors.has('field-country')"
                            class="py-3"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                Please select your country
                            </div>
                        </div>
                    </div>
                    <!-- END Field: Country -->
                </b-col>
            </b-row>

            <b-row>
                <b-col
                    cols="12"
                    lg="6"
                >
                    <!-- Email -->
                    <div class="field-wrap">
                        <label
                            class="h4 text-bold color-mid-grey mb-3"
                            for="offer-email"
                        >
                            Email Address
                        </label>
                        <div class="field-wrap__input">
                            <input
                                id="offer-email"
                                v-model="makeOffer.email"
                                v-validate="{ required: true, email: true }"
                                type="email"
                                name="offer-email"
                                placeholder=""
                            >
                        </div>
                        <div
                            v-show="errors.has('offer-email')"
                            class="validation-error pt-4"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                {{ errors.first('offer-email') }}
                            </div>
                        </div>
                    </div>
                    <!-- END Email -->
                </b-col>
                <b-col
                    cols="12"
                    lg="6"
                >
                    <!-- Telephone -->
                    <div class="field-wrap">
                        <label
                            class="h4 text-bold color-mid-grey mb-3"
                            for="offer-telephone"
                        >
                            Telephone
                        </label>
                        <div class="field-wrap__input">
                            <input
                                id="offer-telephone"
                                v-model="makeOffer.telephone"
                                v-validate="{ required: true }"
                                type="text"
                                name="offer-telephone"
                                placeholder=""
                            >
                        </div>
                        <div
                            v-show="errors.has('offer-telephone')"
                            class="validation-error pt-4"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                {{ errors.first('offer-telephone') }}
                            </div>
                        </div>
                    </div>
                    <!-- END Telephone -->
                </b-col>
            </b-row>

            <h2 class="h4 font-sans-serif mb-3 color-mid-grey text-bold">
                Solicitor
            </h2>

            <b-row
                v-if="solicitors.length"
            >
                <b-col
                    cols="12"
                >
                    <div class="field-wrap">
                        <div class="field-wrap__input field-wrap__input--select mb-5">
                            <select
                                id="field-select-solicitor"
                                v-model="makeOffer.solicitor.id"
                                v-validate="{ required: true }"
                                name="field-select-solicitor"
                            >
                                <option
                                    :value="null"
                                    disabled
                                    selected
                                    hidden
                                >
                                    Select a solicitor
                                </option>
                                <option
                                    v-for="solicitor in solicitors"
                                    :key="solicitor.id"
                                    :value="solicitor.id"
                                >
                                    {{ solicitor.name }}
                                </option>
                                <option value="new">
                                    Add a new solicitor
                                </option>
                            </select>
                        </div>
                        <div
                            v-if="errors.has('field-selectaddress')"
                            class="py-3"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                Please select a solicitor
                            </div>
                        </div>
                    </div>
                </b-col>
            </b-row>

            <b-row
                v-if="!solicitors.length || makeOffer.solicitor.id === 'new'"
                class="pt-3"
            >
                <b-col
                    cols="12"
                >
                    <!-- Field: Company Name -->
                    <div class="field-wrap">
                        <h4 class="mb-3 color-mid-grey text-bold">
                            Company Name
                        </h4>
                        <label
                            for="field-address-1"
                            class="sr-only"
                        >
                            Company Name
                        </label>
                        <div class="field-wrap__input mb-4">
                            <input
                                id="field-company-name"
                                v-model="makeOffer.solicitor.companyName"
                                v-validate="{ required: true }"
                                :class="{ 'has-error': errors.has('field-company-name') }"
                                type="text"
                                name="field-company-name"
                                placeholder="Company Name"
                            >
                        </div>
                        <div
                            v-if="errors.has('field-company-name')"
                            class="py-3"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                {{ errors.first('field-company-name') }}
                            </div>
                        </div>
                    </div>
                    <!-- END Field: Company Name -->
                </b-col>
                <b-col
                    cols="12"
                >
                    <!-- Field: Company Address -->
                    <div class="field-wrap">
                        <h4 class="mb-3 color-mid-grey text-bold">
                            Company Address
                        </h4>
                        <label
                            for="field-company-address"
                            class="sr-only"
                        >
                            Company Address
                        </label>
                        <div class="field-wrap__input mb-4">
                            <textarea
                                id="field-company-address"
                                v-model="makeOffer.solicitor.companyAddress"
                                v-validate="{ required: true }"
                                :class="{ 'has-error': errors.has('field-company-address') }"
                                type="text"
                                name="field-company-address"
                                placeholder="Company Address"
                                rows="5"
                            />
                        </div>
                        <div
                            v-if="errors.has('field-company-address')"
                            class="py-3"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                {{ errors.first('field-company-address') }}
                            </div>
                        </div>
                    </div>
                    <!-- END Field: Company Address -->
                </b-col>
                <b-col
                    cols="12"
                >
                    <!-- Field: Contact Name -->
                    <div class="field-wrap">
                        <h4 class="mb-3 color-mid-grey text-bold">
                            Contact Name
                        </h4>
                        <label
                            for="field-contact-name"
                            class="sr-only"
                        >
                            Contact Name
                        </label>
                        <div class="field-wrap__input mb-4">
                            <input
                                id="field-contact-name"
                                v-model="makeOffer.solicitor.contactName"
                                v-validate="{ required: true }"
                                :class="{ 'has-error': errors.has('field-contact-name') }"
                                type="text"
                                name="field-contact-name"
                                placeholder="Contact Name"
                            >
                        </div>
                        <div
                            v-if="errors.has('field-contact-name')"
                            class="py-3"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                {{ errors.first('field-contact-name') }}
                            </div>
                        </div>
                        <!-- END Field: Contact Name -->
                    </div>
                </b-col>
                <b-col
                    cols="12"
                    lg="6"
                >
                    <!-- Field: Contact Phone -->
                    <div class="field-wrap">
                        <h4 class="mb-3 color-mid-grey text-bold">
                            Contact Phone
                        </h4>
                        <label
                            for="field-contact-phone"
                            class="sr-only"
                        >
                            Contact Phone
                        </label>
                        <div class="field-wrap__input mb-4">
                            <input
                                id="field-contact-phone"
                                v-model="makeOffer.solicitor.contactPhone"
                                v-validate="{ required: true }"
                                :class="{ 'has-error': errors.has('field-contact-phone') }"
                                type="text"
                                name="field-contact-phone"
                                placeholder="Contact Phone"
                            >
                        </div>
                        <div
                            v-if="errors.has('field-contact-phone')"
                            class="py-3"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                {{ errors.first('field-contact-phone') }}
                            </div>
                        </div>
                    </div>
                    <!-- END Field: Town / City -->
                </b-col>
                <b-col
                    cols="12"
                    lg="6"
                >
                    <!-- Field: Contact Email -->
                    <div class="field-wrap">
                        <h4 class="mb-3 color-mid-grey text-bold">
                            Contact Email
                        </h4>
                        <label
                            for="field-contact-email"
                            class="sr-only"
                        >
                            Contact Email
                        </label>
                        <div class="field-wrap__input mb-4">
                            <input
                                id="field-contact-email"
                                v-model="makeOffer.solicitor.contactEmail"
                                v-validate="{ required: true }"
                                :class="{ 'has-error': errors.has('field-contact-email') }"
                                type="text"
                                name="field-contact-email"
                                placeholder="Contact Email"
                            >
                        </div>
                        <div
                            v-if="errors.has('field-contact-email')"
                            class="py-3"
                        >
                            <div class="d-inline-block alert-error p-3 rounded-corners">
                                {{ errors.first('field-contact-email') }}
                            </div>
                        </div>
                    </div>
                    <!-- END Field: Contact Email -->
                </b-col>
            </b-row>

            <!-- Accept privacy -->
            <b-row class="justify-content-center">
                <b-col
                    cols="12"
                    lg="8"
                >
                    <div class="field-wrap field-wrap--agreement">
                        <div class="field-wrap__input field-wrap__input--checkbox">
                            <input
                                id="offer-agree"
                                v-model="makeOffer.agreement"
                                v-validate="{ required: true }"
                                :true-value="true"
                                :false-value="false"
                                type="checkbox"
                                name="offer-agree"
                            >
                            <label
                                for="offer-agree"
                                class="p-sm"
                            >
                                <span class="check-element" />
                                <span>
                                    By submitting the following form you agree for your data to be used in accordance with the terms set out in our
                                    <router-link :to="{ name: 'privacy-policy' }">Privacy Policy</router-link> and
                                    <router-link :to="{ name: 'terms-conditions' }">Terms &amp; Conditions</router-link>
                                </span>
                            </label>
                        </div>
                    </div>
                </b-col>
            </b-row>
            <!-- END Accept privacy -->

            <div class="d-flex justify-content-center">
                <button
                    :class="{ disabled : !makeOffer.agreement }"
                    :disabled="!makeOffer.agreement"
                    class="btn btn--primary"
                    @click="placeOffer()"
                >
                    Place Offer
                </button>
            </div>
        </b-modal>

        <!-- Property lookup modal -->
        <PostcodeLookup
            ref="postcodeLookup"
            @propertySelected="prefillAddress"
            @pageLoaderRemove="hidePageLoader"
        />
        <!-- END Property lookup modal -->
    </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import Flickity from 'vue-flickity';
import RequestViewing from '../components/RequestViewing';
import PostcodeLookup from '../components/PostcodeLookup';
import 'flickity-as-nav-for';
import 'flickity-fullscreen';

export default {
    name: 'ViewProperty',
    components: {
        Flickity,
        RequestViewing,
        PostcodeLookup
    },
    data() {
        return {
            flickityOptions: {
                initialIndex: 0,
                prevNextButtons: false,
                pageDots: false,
                fullscreen: true
            },
            flickityNavOptions: {
                initialIndex: 0,
                prevNextButtons: false,
                pageDots: false,
                asNavFor: '.flickity-main',
                contain: true
            },
            activeImageIndex: 0,
            transactionTypes: {
                sale: 'Sale',
                let: 'Let'
            },
            makeOffer: {
                name: '',
                address: {
                    id: null,
                    address: '',
                    address2: '',
                    town: '',
                    county: '',
                    postcode: '',
                    country: null
                },
                email: '',
                telephone: '',
                conditions: '',
                buyer: null,
                deposit: '',
                amount: '',
                solicitor: {
                    id: null,
                    companyName: '',
                    companyAddress: '',
                    contactName: '',
                    contactPhone: '',
                    contactEmail: ''
                },
                agreement: false
            },
            dialogConfig: {
                title: 'Add Appointment',
                createButtonLabel: 'Add',
                fields: [
                    {
                        name: 'type',
                        type: 'text',
                        required: true,
                        label: 'Appointment Type'
                    },
                    {
                        name: 'name',
                        type: 'text',
                        required: true,
                        label: 'Appointee Name'
                    },
                    {
                        name: 'telephone',
                        type: 'text',
                        label: 'Appointee Telephone'
                    },
                    {
                        name: 'email',
                        type: 'email',
                        label: 'Appointee Email'
                    },
                    {
                        name: 'address',
                        type: 'text',
                        label: 'Property Address'
                    },
                    {
                        name: 'notes',
                        type: 'textarea',
                        label: 'Additional Notes'
                    }
                ]
            }
        };
    },
    computed: {
        ...mapState('global', ['buyerPositions']),
        ...mapState('user', ['user']),
        ...mapGetters('user', ['addresses', 'solicitors']),
        ...mapState('properties', ['property']),
        locationMap() {
            return `https://www.google.com/maps/embed/v1/place?q=${encodeURI(this.property.full_address)}&key=AIzaSyAmUpL6mRXljHjtxm8AFPu29x5ZMGEzIlU`;
        },
        garage() {
            try {
                if (this.property.garages === 1) {
                    return 'Single';
                }

                if (this.property.garages === 2) {
                    return 'Double';
                }

                return false;
            } catch (e) {
                return false;
            }
        }
    },
    mounted() {
        this.getProperty({ propertyId: this.$route.params.id });
    },
    methods: {
        ...mapActions('properties', ['getProperty', 'createOffer']),
        getTransactionType(type) {
            return this.transactionTypes[type] || null;
        },
        placeOffer() {
            this.$validator.validateAll().then((result) => {
                if (result) {
                    this.createOffer({
                        id: this.property.id,
                        name: this.makeOffer.name,
                        email: this.makeOffer.email,
                        telephone: this.makeOffer.telephone,
                        conditions: this.makeOffer.conditions,
                        buyer_position: this.makeOffer.buyer,
                        deposit: this.makeOffer.deposit,
                        amount: this.makeOffer.amount,
                        address: {
                            id: this.makeOffer.address.id,
                            address: this.makeOffer.address.address,
                            address2: this.makeOffer.address.address2,
                            town: this.makeOffer.address.town,
                            county: this.makeOffer.address.county,
                            postcode: this.makeOffer.address.postcode,
                            country: this.makeOffer.address.country
                        },
                        solicitor: {
                            id: this.makeOffer.solicitor.id,
                            company_name: this.makeOffer.solicitor.companyName,
                            company_address: this.makeOffer.solicitor.companyAddress,
                            contact_name: this.makeOffer.solicitor.companyName,
                            contact_phone: this.makeOffer.solicitor.contactPhone,
                            contact_email: this.makeOffer.solicitor.contactEmail
                        }
                    })
                        .then(() => {
                            this.$refs.offerModal.hide();
                            this.$toasted.success('Your offer has been successfully submitted and is now pending.');
                        })
                        .catch(() => {
                            this.$toasted.error('There was an error while attempting to submit your offer.');
                        });
                }
            });
        },
        eventDisplay(event) {
            return `${event.type} - ${event.address}`;
        },
        eventCreated(event) {

        },
        eventClicked(event) {

        },
        showPageLoader() {
            this.pageLoader = true;
        },
        hidePageLoader() {
            this.pageLoader = false;
        },
        prefillAddress(property) {
            // Format town as it seems to come as all capitals
            let town = property.post_town;
            town = town.toLowerCase();
            town = town.charAt(0).toUpperCase() + town.substr(1);

            // Set our address field as the results of what was selected
            this.makeOffer.address.address = property.line_1;
            this.makeOffer.address.address2 = property.line_2;
            this.makeOffer.address.postcode = property.postcode;
            this.makeOffer.address.town = town;
            this.makeOffer.address.county = property.county;
            this.makeOffer.address.country = property.country;
        },
        searchAddress() {
            this.showPageLoader();
            this.$refs.postcodeLookup.findAddress(this.postcodeSearch);
        },
        prepopulateOfferForm() {
            if (!this.addresses.length) {
                this.makeOffer.address.id = 'new';
            }
            if (!this.solicitors.length) {
                this.makeOffer.solicitor.id = 'new';
            }
            this.makeOffer.name = this.user.name;
            this.makeOffer.email = this.user.email;
            this.makeOffer.telephone = this.user.telephone;
        }
    }
};
</script>

<style lang="scss">
.property-features {
    display: flex;
    flex-wrap: wrap;

    li {
        flex: 0 1 100%;
        display: flex;
        align-items: center;
        padding: 1rem;

        @include media-breakpoint-up(sm) {
            flex: 0 1 50%;
        }

        @include media-breakpoint-up(lg) {
            flex: 0 1 33.333%;
        }

        @include media-breakpoint-up(xl) {
            flex: 0 1 25%;
        }
    }
}

.feature-icon {
    width: 3rem;
    color: theme-color('secondary');
}

// Modals
.modal {
    &#modal-request {
        .modal-dialog {
            .modal-content {
                background: theme-color('light-grey');
            }
        }

        .btn {
            width: auto;
        }
    }

    &#modal-offer {
        .modal-dialog {
            .modal-content {
                background: theme-color('light-grey');
            }
        }

        .btn {
            width: auto;
        }
    }
}

// Carousel global

.flickity-slider {
    > div {
        position: relative;
        padding-bottom: 56.25%;

        img {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            object-fit: cover;
            height: 100%;
        }
    }
}

.is-flickity-fullscreen {
    body {
        overflow-y: hidden;
    }
}

// Carousel buttons

.flickity-button {
    &.flickity-fullscreen-button {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 1;
        width: 30px;
        height: 30px;
        font-size: 12px;
        background: white;
    }

    &.flickity-fullscreen-button-view {
        display: block;
    }

    &.flickity-fullscreen-button-exit {
        display: none;
    }
}

// Carousel main

.flickity-main {
    .flickity-slider {
        > div {
            width: 100%;
            padding-bottom: 70%;
        }
    }

    &.is-fullscreen {
        position: fixed;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        z-index: 99999;
        background: white;

        .flickity-slider {
            > div {
                display: flex;
                align-items: center;
                justify-content: center;
                padding-bottom: 0;
                height: 100%;
                background: white;

                img {
                    position: relative;
                    top: auto;
                    left: auto;
                    height: auto;
                    max-height: 80%;
                }
            }
        }

        .flickity-button {
            &.flickity-fullscreen-button-view {
                display: none;
            }

            &.flickity-fullscreen-button-exit {
                display: block;
            }
        }
    }
}

// Carousel nav

.flickity-nav {
    margin-top: 15px;
    margin-left: -7.5px;
    margin-right: -7.5px;

    .flickity-slider {
        > div {
            opacity: .4;
            transition: opacity .3s ease;
            width: 100px;
            padding-bottom: 20%;

            @include media-breakpoint-up(sm) {
                width: 125px;
            }

            @include media-breakpoint-up(md) {
                width: 150px;
            }

            @include media-breakpoint-up(lg) {
                width: 125px;
            }

            @include media-breakpoint-up(xl) {
                width: 200px;
            }

            &.is-selected {
                opacity: 1;
            }

            img {
                padding-left: 7.5px;
                padding-right: 7.5px;
            }
        }
    }
}

.page-view-property .btn:not(.btn--sm) {
    width: 100%;
    display: block;
    min-width: 0;
}

.location-map {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
}

// Print stylesheet
@media print {
    .page-view-property {
        // hide header sections
        .header {
            box-shadow: none !important;
        }

        .header-items {
            display: none !important;
        }

        .sidebar {
            display: none !important;
        }

        .footer {
            display: none !important;
        }

        .sidebar-sticky {
            display: none !important;
        }

        // global
        img {
            max-width: 100% !important;
            height: auto !important;
        }

        // thumbnail slider
        .thumbnail-slider {
            display: none !important;
        }

        .thumbnail-main {
            margin-bottom: 30px !important;
            padding-bottom: 0 !important;

            > div {
                position: relative !important;
                top: auto !important;
                left: auto !important;
            }

            img {
                width: auto !important;
                object-fit: none !important;
                max-height: 400px !important;
            }
        }

        .property-features {
            li {
                flex: 0 1 25% !important;
            }
        }

        // cards
        .card {
            display: block !important;
            background: transparent !important;
        }

        .card__body {
            padding: 0 !important;
        }

        .card.card--video {
            display: none !important;
        }
    }
}
</style>
